import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Typography
} from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataProviderInstance } from '../../api/DataProvider';
import { isOwner } from '../../utils/RoleManager';
import styles from './TermsReacceptanceDialog.module.css';

/**
 * Modal de reaceite dos Termos de Uso / Política de Privacidade.
 *
 * Montado uma vez em src/header/Header.js (mesmo padrão global de TrialBanner e
 * PastDueBanner). Ao carregar o app, se o usuário atual for COMPANY_OWNER, verifica
 * via CheckTermsAccepted se a empresa já aceitou a versão vigente dos termos
 * (ver public/api/company/terms/TermsVersion.php). Se não tiver aceitado, abre um
 * diálogo não-dispensável cobrando o reaceite antes de continuar usando o sistema.
 *
 * Outros papéis (COMPANY_ADMIN, COMPANY_STAFF etc.) nunca veem este modal.
 */
const TermsReacceptanceDialog = () => {
  const { CheckTermsAccepted, AcceptTerms } = DataProviderInstance();
  // isOwner() lê o contexto do DataProvider (hook), então precisa ser chamado
  // no corpo do componente, nunca dentro do useEffect.
  const ownerLoggedIn = isOwner();

  const [open, setOpen] = useState(false);
  // 'ownership_transfer' = a pessoa acabou de receber a conta e aceita em nome próprio
  const [reason, setReason] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Só o dono da empresa é cobrado pelo reaceite.
    if (!ownerLoggedIn) {
      return;
    }

    let isActive = true;

    CheckTermsAccepted((response) => {
      if (!isActive) return;

      if (response && response.success && response.accepted_current === false) {
        // Conta que nunca aceitou (ex.: criada antes do aceite no cadastro) não vê
        // "Atualizamos nossos termos": só quem já aceitou uma versão anterior vê
        setReason(response.reason || (response.accepted === false ? 'first_acceptance' : null));
        setOpen(true);
      }
    });

    return () => {
      isActive = false;
    };
    // Roda quando a sessão confirma que o usuário é o dono (Header não é
    // remontado entre rotas da área autenticada, então é uma checagem só).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerLoggedIn]);

  const handleOpenTerms = () => {
    window.open('/terms-privacy', '_blank', 'noopener,noreferrer');
  };

  const handleAccept = () => {
    if (!checked || submitting) return;

    setSubmitting(true);
    setError('');

    AcceptTerms((response) => {
      setSubmitting(false);

      if (response && response.success) {
        setOpen(false);
      } else {
        setError(
          (response && response.error) ||
          'Não foi possível registrar o aceite dos termos. Tente novamente.'
        );
      }
    });
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="terms-reacceptance-title"
      PaperProps={{ className: styles.paper }}
    >
      <DialogTitle id="terms-reacceptance-title" className={styles.title}>
        <Box className={styles.iconWrapper}>
          <VerifiedUserOutlinedIcon className={styles.headerIcon} />
        </Box>
        {reason ? 'Confirme os termos' : 'Atualizamos nossos termos'}
      </DialogTitle>

      <DialogContent className={styles.content}>
        <Typography variant="body1" className={styles.description}>
          {reason === 'ownership_transfer'
            ? 'Você agora é responsável pela conta da clínica. Para continuar usando a Gabriela, confirme que está de acordo com os Termos de Uso e a Política de Privacidade.'
            : reason === 'first_acceptance'
            ? 'Para continuar usando a Gabriela, confirme que você está de acordo com os Termos de Uso e a Política de Privacidade.'
            : 'Revisamos os Termos de Uso e a Política de Privacidade. Para continuar usando a Gabriela, confirme que você está de acordo com a nova versão.'}
        </Typography>

        <button type="button" className={styles.documentLink} onClick={handleOpenTerms}>
          <DescriptionOutlinedIcon className={styles.documentIcon} />
          <span className={styles.documentText}>
            <span className={styles.documentTitle}>Termos de Uso e Política de Privacidade</span>
            <span className={styles.documentHint}>Abre em uma nova aba</span>
          </span>
          <OpenInNewIcon className={styles.openIcon} />
        </button>

        <FormControlLabel
          className={styles.acceptLabel}
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
          }
          label="Li e aceito os Termos de Uso e a Política de Privacidade"
        />

        {error && (
          <Alert severity="error" className={styles.error}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button
          variant="contained"
          disabled={!checked || submitting}
          onClick={handleAccept}
          className={styles.acceptButton}
        >
          {submitting ? 'Enviando...' : 'Aceitar e continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsReacceptanceDialog;
